// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file presenceService.js
 * @description Servicio centralizado para actualización de estado de presencia
 * Unifica: Backend PHP + WebSocket + Estado React + UI
 * @version 1.0.0
 *
 * FLUJO AUTH (trazabilidad 401):
 * PresenceModal → updatePresence() → PresenceContext → updatePresenceService()
 * → apiService.request(PRESENCE, { method: 'PUT', body }) → api.js request()
 * → defaultOptions.headers = { ...authHeaders(), ... } → apiClientRequest(url, defaultOptions)
 * → apiClient.request() → headers = { ...options.headers, ...authHeaders() } → fetch(url, { headers })
 * → nginx → fastcgi_param HTTP_AUTHORIZATION $http_authorization → backend presence/status.php
 * → $_SERVER['HTTP_AUTHORIZATION'] / getallheaders() → AuthHelper::obtenerUsuarioPorToken() → sesiones DB
 * Punto de fallo conocido: nginx no reenvía Authorization a PHP-FPM si no se setea fastcgi_param.
 */

import { apiService } from './api';
import { getToken, authHeaders } from './apiClient';
import { API_ENDPOINTS } from '../utils/constants';
import { dispatchPresenceUpdated } from './presenceRealtimeEvents';
import { devLog } from '../utils/devConsoleLog';

/**
 * Estados de presencia válidos
 */
export const PRESENCE_STATUSES = {
  ONLINE: 'online',
  IDLE: 'idle',
  DND: 'dnd',
  BUSY: 'busy',
  OFFLINE: 'offline',
  CUSTOM: 'custom'
};

/**
 * Valida que el status sea válido
 * @param {string} status - Estado a validar
 * @returns {boolean} True si es válido
 */
export function isValidPresenceStatus(status) {
  return Object.values(PRESENCE_STATUSES).includes(status);
}

/**
 * Actualiza el estado de presencia del usuario
 * Función centralizada que:
 * 1. Valida el status
 * 2. Envía al backend PHP
 * 3. El backend guarda en DB y emite WebSocket
 * 4. Retorna resultado para actualizar estado React
 * 
 * @param {number} usuarioId - ID del usuario
 * @param {string} status - Estado de presencia (online, idle, dnd, busy, offline, custom)
 * @param {Object} customData - Datos para estado custom (mensaje, emoji, expira_en)
 * @returns {Promise<Object>} { success: boolean, presence?: Object, error?: string }
 */
export async function updateUserPresence(usuarioId, status, customData = null) {
  // 🔥 PRESENCE SERVICE — Log de inicio
  console.log('🔥 [PRESENCE SERVICE] updateUserPresence llamado', {
    usuarioId,
    status,
    hasCustomData: !!customData,
    timestamp: new Date().toISOString()
  });

  // Validar usuario
  if (!usuarioId) {
    const error = 'Usuario no identificado';
    console.error('🔥 [PRESENCE SERVICE] ERROR:', error);
    return { success: false, error };
  }

  // Validar status
  if (!isValidPresenceStatus(status)) {
    const error = `Status inválido: ${status}. Debe ser uno de: ${Object.values(PRESENCE_STATUSES).join(', ')}`;
    console.error('🔥 [PRESENCE SERVICE] ERROR:', error);
    return { success: false, error };
  }

  try {
    // Preparar payload para backend
    const payload = {
      usuario_id: usuarioId,
      status
    };

    // Si es custom, agregar datos adicionales
    if (status === PRESENCE_STATUSES.CUSTOM && customData) {
      payload.custom_status = customData.mensaje || customData.custom_status || null;
      payload.custom_emoji = customData.emoji || customData.custom_emoji || null;
      payload.expires_at = customData.expira_en || customData.expires_at || null;
      payload.preset_key = customData.preset_key != null ? customData.preset_key : null;
    } else if (status !== PRESENCE_STATUSES.CUSTOM) {
      // Si no es custom, limpiar campos custom
      payload.custom_status = null;
      payload.custom_emoji = null;
      payload.expires_at = null;
      payload.preset_key = null;
    }

    const endpoint = API_ENDPOINTS.PRESENCE;
    const finalHeaders = authHeaders();
    if (import.meta.env.DEV) {
      devLog('[AUTH TRACE FRONT]', { url: endpoint, tokenPresent: !!getToken() });
    }

    // Llamar al backend vía cliente central (api.js → apiClient; inyecta Authorization: Bearer)
    let response;
    try {
      response = await apiService.request(endpoint, {
        method: 'PUT',
        body: JSON.stringify(payload),
        cache: 'no-store',
      });
    } catch (requestError) {
      console.error('[PRESENCE SERVICE] ❌ Error en request:', requestError);
      console.error('[PRESENCE SERVICE] ❌ Request error details:', {
        message: requestError.message,
        stack: requestError.stack,
        endpoint,
        payload
      });
      return { success: false, error: requestError.message || 'Error de conexión' };
    }

    console.log('[PRESENCE SERVICE] RAW RESPONSE:', response);
    console.log('[PRESENCE SERVICE] Recibido:', response);

    // 🔥 PRESENCE SERVICE — Validación robusta de respuesta
    if (!response) {
      console.error('[PRESENCE SERVICE] ❌ RESPUESTA UNDEFINED');
      console.error('[PRESENCE SERVICE] ❌ Response es null/undefined');
      throw new Error('NO RESPONSE FROM SERVER');
    }

    if (typeof response !== 'object') {
      console.error('[PRESENCE SERVICE] ❌ Respuesta no es objeto:', typeof response, response);
      return { success: false, error: 'Respuesta inválida del servidor (no es objeto)' };
    }

    if (!response.success) {
      console.error('[PRESENCE SERVICE] ❌ Error backend:', response);
      return { success: false, error: response.error || 'Error desconocido' };
    }

    console.log('[PRESENCE SERVICE] ✅ Actualización exitosa:', response);

    // Normalizar respuesta del backend
    const presenceData = response.presence || response.data || {};
    const normalizedPresence = {
      usuario_id: presenceData.usuario_id || usuarioId,
      status: presenceData.status || status,
      custom_status: presenceData.custom_status || null,
      custom_emoji: presenceData.custom_emoji || null,
      expires_at: presenceData.expires_at || null,
      preset_key: presenceData.preset_key != null ? presenceData.preset_key : null,
    };

    dispatchPresenceUpdated({
      user_id: usuarioId,
      status: normalizedPresence.status,
      custom_status: normalizedPresence.custom_status,
      custom_emoji: normalizedPresence.custom_emoji,
      preset_key: normalizedPresence.preset_key,
      expires_at: normalizedPresence.expires_at,
      source: 'put',
    });

    // Retornar respuesta normalizada
    return {
      success: true,
      presence: normalizedPresence
    };

  } catch (error) {
    const errorMsg = error.message || 'Error de conexión';
    console.error('🔥 [PRESENCE SERVICE] EXCEPCIÓN:', {
      error: errorMsg,
      stack: error.stack,
      usuarioId,
      status
    });
    return { success: false, error: errorMsg };
  }
}

/**
 * Obtiene el estado de presencia de un usuario
 * @param {number} usuarioId - ID del usuario
 * @returns {Promise<Object>} Estado de presencia
 */
export async function getUserPresence(usuarioId) {
  if (!usuarioId) {
    return { success: false, error: 'Usuario no identificado' };
  }

  try {
    const response = await apiService.getPresence(usuarioId);
    
    if (response && response.success) {
      const presenceData = response.presence || response.data || {};
      return {
        success: true,
        presence: {
          usuario_id: presenceData.usuario_id || usuarioId,
          status: presenceData.status || presenceData.presence_status || 'online',
          custom_status: presenceData.custom_status || null,
          custom_emoji: presenceData.custom_emoji || null,
          expires_at: presenceData.expires_at || presenceData.custom_status_expires_at || null,
          preset_key: presenceData.preset_key != null ? presenceData.preset_key : null,
        }
      };
    }

    return { success: false, error: response?.error || 'Error al obtener presencia' };
  } catch (error) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
}
